import { Select } from "antd";
import { useEffect, useState, useMemo, useRef } from "react";
import { PieChart, Pie, Cell, Legend, Tooltip, Sector, ResponsiveContainer } from "recharts";
import { companiesService, dashboardPresencaService, departmentsService } from "./../../../redux/services";

const C_ATIVOS = "#00B6FF";
const C_DESLIG = "#E100B6";
const colors = ["#FF0000", "#008000", "#FFA500"];

const LegendDot = ({ color }) => (
  <span
    style={{
      display: "inline-block",
      width: 10,
      height: 10,
      borderRadius: 999,
      background: color,
      marginRight: 6
    }}
  />
);
function ActiveSlice(props) {
  return <Sector {...props} outerRadius={props.outerRadius + 12} />;
}

function PlaceholderDonut() {
  return (
    <div style={{ width: "100%", height: 308, position: "relative", display: "grid", placeItems: "center" }}>
      <svg
        style={{
          position: "absolute",
          left: "36%",
          top: "50%",
          transform: "translate(-50%, -50%)",
        }}
        width="220"
        height="220"
        viewBox="0 0 220 220"
        role="img"
        aria-label="Nenhum registro encontrado"
      >
        <circle
          cx="110"
          cy="110"
          r="80"
          fill="none"
          stroke="#F2F4F7"
          strokeWidth="24"
        />
        <circle
          cx="110"
          cy="110"
          r="80"
          fill="none"
          stroke="#E5E7EB"
          strokeWidth="24"
          strokeLinecap="round"
          strokeDasharray="220 440"
          style={{ animation: "dashMove 2.2s linear infinite" }}
        />
        <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle" fill="#98A2B3" fontSize="14">
          <tspan x="50%" dy="-0.4em">Nenhum registro</tspan>
          <tspan x="50%" dy="1.2em">encontrado</tspan>
        </text>
      </svg>

    </div>
  );
}

export default function DonutChart() {

  const [filter, setFilter] = useState(false);
  const [modalFilter, setModalFilter] = useState(false);

  const [emp, setEmp] = useState("all");
  const [dept, setDept] = useState("");

  const [empDraft, setEmpDraft] = useState("");
  const [deptDraft, setDeptDraft] = useState("");

  const [loadingChart, setLoadingChart] = useState(false);
  const [chartData, setChartData] = useState([]);

  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [empresas, setEmpresas] = useState([
    { label: "Todas as empresas", value: "all" },
  ]);

  const [loadingDepartamentos, setLoadingDepartamentos] = useState(false);
  const [departamentos, setDepartamentos] = useState([
    { label: "Todos os Departamentos", value: "" },
  ]);

  const isAll = (v) => v === "" || v === "all" || v == null;

  async function carregaEmpresas() {
    setLoadingEmpresas(true);
    try {
      const response = await companiesService.getAll();
      const brutos = response?.data?.data ?? response?.data ?? response ?? [];
      const lista = (Array.isArray(brutos) ? brutos : []).map((d) => ({
        label: d.nome,
        value: d.uuid,
      }));
      setEmpresas((prev) => [prev[0], ...lista]);
    } catch (e) {
      console.error("Erro ao carregar empresas", e);
      setEmpresas([{ label: "Todas as empresas", value: "all" }]);
    } finally {
      setLoadingEmpresas(false);
    }
  }

  async function carregarDepartamentos(empUuid) {
    setLoadingDepartamentos(true);
    try {
      const response = await departmentsService.getAll(
        isAll(empUuid) ? undefined : empUuid
      );
      const brutos = response?.data?.data ?? response?.data ?? response ?? [];
      const lista = (Array.isArray(brutos) ? brutos : []).map((d) => ({
        label: d.nome,
        value: d.uuid,
      }));
      setDepartamentos([{ label: "Todos os Departamentos", value: "" }, ...lista]);
    } catch (e) {
      console.error("Erro ao carregar departamentos", e);
      setDepartamentos([{ label: "Todos os Departamentos", value: "" }]);
    } finally {
      setLoadingDepartamentos(false);
    }
  }

  async function carregaDados(nextEmp = emp, nextDept = dept) {
    setLoadingChart(true);
    try {
      const options = {};
      if (!isAll(nextEmp)) options.empresa = nextEmp;
      if (!isAll(nextDept)) options.departamento = nextDept;

      const resp = await dashboardPresencaService.getAll(options);

      const presenca =
        resp?.data?.data?.presenca ??
        resp?.data?.presenca ??
        resp?.presenca ??
        [];

      const arr = Array.isArray(presenca) ? presenca : [];
      const lista = arr.map((it) => ({
        name: String(it.name ?? it.nome ?? ""),
        value: Number(it.value ?? it.total ?? 0),
      }));

      setChartData(lista);
    } catch (e) {
      console.error("Erro ao carregar dados do gráfico", e);
      setChartData([]);
    } finally {
      setLoadingChart(false);
    }
  }

  function openFilter() {
    setEmpDraft(emp || "");
    setDeptDraft(dept || "");
    setModalFilter(true);
  }

  async function applyFilter() {
    const nextEmp = empDraft || "all";
    const nextDept = deptDraft || "";
    setEmp(nextEmp);
    setDept(nextDept);
    await carregaDados(nextEmp, nextDept); // evita estado antigo
    setModalFilter(false);
  }

  function cancelFilter() {
    setModalFilter(false);
  }

  const didInitRef = useRef(false);

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    (async () => {
      await carregaEmpresas();
      await carregarDepartamentos("");
      await carregaDados(emp, dept);
    })();
  }, []);

  useEffect(() => {
    if (!modalFilter) return;
    if (empDraft == null) return;

    if (empDraft === emp) {
      if (!empDraft || empDraft === "all") {
        setDepartamentos([{ label: "Todos os Departamentos", value: "" }]);
      } else {
        carregarDepartamentos(empDraft);
      }
      return;
    }

    setDeptDraft("");
    if (!empDraft || empDraft === "all") {
      setDepartamentos([{ label: "Todos os Departamentos", value: "" }]);
      return;
    }
    carregarDepartamentos(empDraft);
  }, [empDraft, modalFilter, emp]);

  function CustomLegend({ payload = [] }) {
    return (
      <ul style={{
        listStyle: "none",
        margin: 0,
        padding: 0,
        display: "grid",
        rowGap: 24,
        columnGap: 16,
      }}>
        {payload.map((item) => (
          <li key={item.value} style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span
              style={{
                width: 10, height: 10, borderRadius: "50%",
                background: item.color, display: "inline-block", flex: "0 0 auto",
              }}
            />
            <span style={{ color: "#475467", fontSize: 14 }}>{item.value}</span>
          </li>
        ))}
      </ul>
    );
  }

  const [activeIndex, setActiveIndex] = useState(null);

  const hasData = useMemo(() => chartData.some((d) => (d?.value ?? 0) > 0), [chartData]);
  const total = useMemo(() => chartData.reduce((acc, d) => acc + (d?.value ?? 0), 0), [chartData]);

  return (
    <div
      style={{
        width: "100%",
        background: "#fff",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8
        }}
      >
        <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
          Estatísticas de Presença:
        </div>
      </div>
      <div
        className="filter-icon"
        style={{
          position: "absolute",
          top: 23,
          right: 23,
          zIndex: 10
        }}
      >
        <i
          onClick={openFilter}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openFilter()}
          className="fas fa-filter"
          style={{ fontSize: 18, cursor: "pointer", color: "#00C9FF" }}
        />

        {modalFilter && (
          <div
            style={{
              position: "absolute",
              right: 0,
              top: -8,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: 16,
              zIndex: 999,
              background: "#fff",
              borderRadius: 8,
              boxShadow: "0 8px 24px rgba(16,24,40,0.12)",
              padding: 16,
            }}
          >
            <Select
              value={empDraft}
              onChange={setEmpDraft}
              options={empresas}
              size="middle"
              style={{ minWidth: 220 }}
              loading={loadingEmpresas}
            />
            <Select
              value={deptDraft}
              onChange={setDeptDraft}
              options={departamentos}
              size="middle"
              style={{ minWidth: 220 }}
              loading={loadingDepartamentos}
            />
            <div style={{ display: "flex", gap: 8, width: "100%" }}>
              <button
                type="button"
                className="ant-btn"
                style={{ flex: 1 }}
                onClick={cancelFilter}
              >
                <span>Cancelar</span>
              </button>
              <button
                type="button"
                className="ant-btn ant-btn-primary"
                style={{ flex: 1 }}
                onClick={applyFilter}
              >
                <span>Filtrar</span>
              </button>
            </div>
          </div>
        )}
      </div>
      {
        !hasData ? (
          <PlaceholderDonut />
        ) : (
          <div style={{ width: "100%", height: 308, overflow: "visible" }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="36%"
                  cy="50%"
                  innerRadius="48%"
                  outerRadius="70%"
                  paddingAngle={3}
                  dataKey="value"
                  labelLine={false}
                  label={({ percent }) => `${(percent * 100).toFixed(0)}%`} // corrigido
                  activeIndex={activeIndex}
                  activeShape={(props) => <ActiveSlice {...props} />}
                  onMouseEnter={(_, idx) => setActiveIndex(idx)}
                  onMouseLeave={() => setActiveIndex(null)}
                  isAnimationActive
                  animationDuration={400}
                  animationEasing="ease-out"
                >
                  {chartData.map((d, i) => (
                    <Cell key={d.name} fill={colors[i % colors.length]} />
                  ))}
                </Pie>

                <Tooltip
                  formatter={(value, name) => {
                    const total = chartData.reduce((acc, d) => acc + d.value, 0);
                    const percent = ((value / total) * 100).toFixed(0);
                    return [`${percent}%`, name]; // [valor exibido, label da legenda]
                  }}
                />
                <Legend
                  layout="vertical"
                  verticalAlign="middle"
                  align="right"
                  content={<CustomLegend />}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )
      }

      {
        loadingChart && (
          <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", background: "rgba(255,255,255,0.6)" }}>
            Carregando…
          </div>
        )
      }
    </div >
  );
}